/**
 * Authenticated API client for the DWP Platform API.
 * Attaches the Keycloak access token; forces re-login on 401.
 */
import { getValidAccessToken, logout } from './auth'

export const API_BASE = 'https://api.zentrydigitalmm.com'

/** Server-paginated list response. */
export interface Page<T> {
  data: T[]
  total: number
  page: number
  size: number
  totalPages: number
}

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getValidAccessToken()
  if (!token) {
    logout()
    throw new Error('Not authenticated')
  }

  const headers = new Headers(init.headers)
  headers.set('Authorization', `Bearer ${token}`)
  // default JSON content type when sending a serialized body
  if (typeof init.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers })
  if (res.status === 401) {
    logout()
    throw new Error('Session expired')
  }
  return res
}

export async function apiJson<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await apiFetch(path, init)
  if (res.status === 403) {
    throw new Error('You do not have permission to perform this action')
  }
  if (!res.ok) {
    throw new Error(`API error ${res.status}: ${await res.text().catch(() => '')}`)
  }
  return res.json() as Promise<T>
}
